import React from 'react';

/**
 * Alerts Panel - Real-time alerts and warnings
 * Displays critical, warning, and info level alerts
 */
const AlertsPanel = React.memo(function AlertsPanel({ telemetryData = {}, drones = [], selectedDroneId = null, telemetry: selectedTelemetry = null }) {
    const generateAlerts = () => {
        const alerts = [];

        // If a drone is selected, only show alerts for that drone
        const targetDrones = selectedDroneId 
            ? drones.filter(d => d.id === selectedDroneId) 
            : drones;

        targetDrones.forEach(drone => {
            const telemetry = selectedDroneId ? (selectedTelemetry || telemetryData[drone.id]) : telemetryData[drone.id];
            if (!telemetry) return;

            const droneId = drone.id;
            const droneName = drone.name || droneId;
            const timestamp = new Date().toLocaleTimeString();

            // The server's alerts are authoritative: they use the drone's own
            // thresholds and cover what this panel cannot see (corridor breach,
            // altitude fence, speed, stale telemetry). The local checks below
            // are only a fallback for telemetry that arrived without them.
            if (Array.isArray(telemetry.serverAlerts)) {
                const severityOf = { CRITICAL: 'critical', WARNING: 'warning' };
                telemetry.serverAlerts.forEach((a, i) => {
                    alerts.push({
                        id: `${droneId}-srv-${a.category}-${i}`,
                        droneId,
                        droneName,
                        severity: severityOf[a.level] || 'info',
                        icon: a.level === 'CRITICAL' ? '🔴' : '⚠️',
                        message: a.message,
                        timestamp
                    });
                });
                if (telemetry.armed) {
                    alerts.push({
                        id: `${droneId}-armed`,
                        droneId,
                        droneName,
                        severity: 'info',
                        icon: '✈️',
                        message: `Drone armed - ${telemetry.mode || 'N/A'} mode`,
                        timestamp
                    });
                }
                return;
            }

            // Critical Alerts
            if (!telemetry.connected) {
                alerts.push({
                    id: `${droneId}-connection`,
                    droneId,
                    droneName,
                    severity: 'critical',
                    icon: '🔴',
                    message: `Connection lost`,
                    timestamp
                });
            }

            if (telemetry.battery_remaining && telemetry.battery_remaining < 10) {
                alerts.push({
                    id: `${droneId}-battery-critical`,
                    droneId,
                    droneName,
                    severity: 'critical',
                    icon: '🔋',
                    message: `Critical battery: ${telemetry.battery_remaining}%`,
                    timestamp
                });
            }

            if (telemetry.gps_fix && telemetry.gps_fix < 3) {
                alerts.push({
                    id: `${droneId}-gps-nofix`,
                    droneId,
                    droneName,
                    severity: 'critical',
                    icon: '📡',
                    message: `GPS no fix`,
                    timestamp
                });
            }

            // Warning Alerts
            if (telemetry.battery_remaining && telemetry.battery_remaining >= 10 && telemetry.battery_remaining < 20) {
                alerts.push({
                    id: `${droneId}-battery-low`,
                    droneId,
                    droneName,
                    severity: 'warning',
                    icon: '⚠️',
                    message: `Low battery: ${telemetry.battery_remaining}%`,
                    timestamp
                });
            }

            if (telemetry.satellites && telemetry.satellites < 6) {
                alerts.push({
                    id: `${droneId}-satellites-low`,
                    droneId,
                    droneName,
                    severity: 'warning',
                    icon: '📡',
                    message: `Low satellites: ${telemetry.satellites}`,
                    timestamp
                });
            }

            // Info Alerts
            if (telemetry.armed) {
                alerts.push({
                    id: `${droneId}-armed`,
                    droneId,
                    droneName,
                    severity: 'info',
                    icon: '✈️',
                    message: `Drone armed - ${telemetry.mode || 'N/A'} mode`,
                    timestamp
                });
            }
        });

        // Sort by severity
        return alerts.sort((a, b) => {
            const severityOrder = { critical: 0, warning: 1, info: 2 };
            return severityOrder[a.severity] - severityOrder[b.severity];
        });
    };

    const alerts = generateAlerts();

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'var(--danger)';
            case 'warning': return 'var(--warning)';
            case 'info': return 'var(--accentSecondary)';
            default: return 'var(--textSecondary)';
        }
    };

    const getSeverityBg = (severity) => {
        switch (severity) {
            case 'critical': return 'rgba(239, 68, 68, 0.1)';
            case 'warning': return 'rgba(245, 158, 11, 0.1)';
            case 'info': return 'rgba(59, 130, 246, 0.1)';
            default: return 'var(--bgSecondary)';
        }
    };

    const AlertBadge = ({ count, severity }) => {
        if (count === 0) return null;

        return (
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                background: getSeverityBg(severity),
                border: `1px solid ${getSeverityColor(severity)}`,
                fontSize: '10px',
                fontWeight: 700,
                color: getSeverityColor(severity),
                textTransform: 'uppercase'
            }}>
                <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: getSeverityColor(severity),
                    animation: severity === 'critical' ? 'pulse 2s infinite' : 'none'
                }} />
                {count} {severity}
            </div>
        );
    };

    const criticalCount = alerts.filter(a => a.severity === 'critical').length;
    const warningCount = alerts.filter(a => a.severity === 'warning').length;
    const infoCount = alerts.filter(a => a.severity === 'info').length;

    return (
        <div style={{
            background: 'var(--bgPrimary)',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <div style={{
                padding: '10px 12px',
                borderBottom: '1px solid var(--borderSecondary)',
                background: 'var(--bgSecondary)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                }}>
                    🚨 Alerts
                </h3>

                {/* Alert Summary */}
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                }}>
                    <AlertBadge count={criticalCount} severity="critical" />
                    <AlertBadge count={warningCount} severity="warning" />
                    <AlertBadge count={infoCount} severity="info" />
                    {alerts.length === 0 && (
                        <div style={{
                            fontSize: '11px',
                            color: 'var(--success)',
                            fontWeight: 600
                        }}>
                            ✓ All systems nominal
                        </div>
                    )}
                </div>
            </div>

            {/* Alerts List */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden'
            }}>
                {alerts.length === 0 ? (
                    <div style={{
                        padding: '40px 16px',
                        textAlign: 'center',
                        color: 'var(--textSecondary)',
                        fontSize: '12px'
                    }}>
                        No active alerts
                    </div>
                ) : (
                    alerts.map((alert, index) => (
                        <div
                            key={`${alert.id}-${index}`}
                            style={{
                                padding: '12px 16px',
                                borderBottom: '1px solid var(--bgSecondary)',
                                background: getSeverityBg(alert.severity),
                                borderLeft: `3px solid ${getSeverityColor(alert.severity)}`
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                marginBottom: '4px'
                            }}>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: getSeverityColor(alert.severity),
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span>{alert.icon}</span>
                                    <span>{alert.droneName}</span>
                                </div>
                                <div style={{
                                    fontSize: '9px',
                                    color: 'var(--textSecondary)',
                                    fontFamily: 'monospace'
                                }}>
                                    {alert.timestamp}
                                </div>
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: 'var(--textPrimary)',
                                fontWeight: 600
                            }}>
                                {alert.message}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
            `}</style>
        </div>
    );
});

export default AlertsPanel;
